import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertCircle,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Pencil,
  PlusCircle,
} from 'lucide-react';
import { api, LitLegionAdminTask } from '@/lib/api';

interface TreeNode {
  task: LitLegionAdminTask;
  children: TreeNode[];
}

function buildForest(tasks: LitLegionAdminTask[]): TreeNode[] {
  const byId = new Map<string, TreeNode>();
  for (const task of tasks) {
    byId.set(task.taskId, { task, children: [] });
  }

  const roots: TreeNode[] = [];
  for (const node of byId.values()) {
    const parentId = node.task.parentId;
    // Treat tasks whose parent is missing (or null) as roots — defensive against
    // orphans left by deleted parents in older data.
    if (parentId && byId.has(parentId)) {
      byId.get(parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const sortByName = (a: TreeNode, b: TreeNode) => a.task.name.localeCompare(b.task.name);
  const sortDeep = (nodes: TreeNode[]) => {
    nodes.sort(sortByName);
    for (const n of nodes) sortDeep(n.children);
  };
  sortDeep(roots);

  return roots;
}

function countDescendants(node: TreeNode): number {
  let count = 0;
  for (const child of node.children) {
    count += 1 + countDescendants(child);
  }
  return count;
}

interface TreeNodeRowProps {
  node: TreeNode;
  depth: number;
  expanded: Set<string>;
  toggle: (taskId: string) => void;
}

function TreeNodeRow({ node, depth, expanded, toggle }: TreeNodeRowProps) {
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(node.task.taskId);
  const descendantCount = useMemo(() => countDescendants(node), [node]);

  return (
    <>
      <div
        className="flex items-center gap-2 py-2 px-2 rounded hover:bg-muted/50 transition-colors"
        style={{ paddingLeft: `${depth * 1.5 + 0.5}rem` }}
      >
        {hasChildren ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={() => toggle(node.task.taskId)}
            aria-label={isOpen ? 'Collapse' : 'Expand'}
          >
            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        ) : (
          <div className="h-6 w-6 shrink-0" />
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium truncate">{node.task.name}</span>
            {node.task.taskType && (
              <Badge variant="secondary" className="text-xs">
                {node.task.taskType}
              </Badge>
            )}
            {node.task.category && (
              <Badge variant="outline" className="text-xs">
                {node.task.category}
              </Badge>
            )}
            {hasChildren && (
              <span className="text-xs text-muted-foreground">
                {node.children.length} child{node.children.length === 1 ? '' : 'ren'}
                {descendantCount !== node.children.length ? ` · ${descendantCount} total` : ''}
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground">{node.task.xpReward} XP</div>
        </div>

        <Link
          to={`/a/arche_lit_legion/admin/tasks/${node.task.taskId}/edit`}
          aria-label={`Edit ${node.task.name}`}
        >
          <Button variant="ghost" size="icon" className="shrink-0">
            <Pencil className="h-4 w-4" />
          </Button>
        </Link>
      </div>

      {hasChildren && isOpen && (
        <>
          {node.children.map((child) => (
            <TreeNodeRow
              key={child.task.taskId}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              toggle={toggle}
            />
          ))}
        </>
      )}
    </>
  );
}

function collectAllIds(nodes: TreeNode[], acc: Set<string> = new Set()): Set<string> {
  for (const n of nodes) {
    acc.add(n.task.taskId);
    collectAllIds(n.children, acc);
  }
  return acc;
}

export function AdminTaskTreePage() {
  const [tasks, setTasks] = useState<LitLegionAdminTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await api.adminListAllLitLegionTasks();
        if (cancelled) return;
        setTasks(result.tasks);
        // Default: expand every node so the tree is fully visible on first load.
        setExpanded(collectAllIds(buildForest(result.tasks)));
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load tasks');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const forest = useMemo(() => buildForest(tasks), [tasks]);

  const toggle = (taskId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const expandAll = () => setExpanded(collectAllIds(forest));
  const collapseAll = () => setExpanded(new Set());

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to="/a/arche_lit_legion/admin"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Admin Dashboard
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Task Tree</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {tasks.length} task{tasks.length !== 1 ? 's' : ''} · {forest.length} root
              {forest.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={expandAll} disabled={loading}>
              Expand all
            </Button>
            <Button variant="outline" size="sm" onClick={collapseAll} disabled={loading}>
              Collapse all
            </Button>
            <Link to="/a/arche_lit_legion/admin/tasks/create">
              <Button size="sm">
                <PlusCircle className="h-4 w-4 mr-2" />
                Create Task
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10 rounded" />
          ))}
        </div>
      ) : forest.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground mb-4">No tasks created yet.</p>
            <Link to="/a/arche_lit_legion/admin/tasks/create">
              <Button variant="outline">
                <PlusCircle className="h-4 w-4 mr-2" />
                Create your first task
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-2">
            {forest.map((root) => (
              <TreeNodeRow
                key={root.task.taskId}
                node={root}
                depth={0}
                expanded={expanded}
                toggle={toggle}
              />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
